import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { normalizeStoredLanguage, readStoredLanguage } from '../i18n/language-storage';
import { TokenStorageService } from '../storage/token-storage.service';

const AUTHORIZATION_EXCLUDED_ENDPOINTS = [
  '/api/Accounts/Login',
  '/api/Accounts/LoginByTenantId',
  '/api/Accounts/LoginOptions',
  '/api/Accounts/Entra/Exchange',
  '/api/Accounts/Logout',
  '/api/Accounts/ForgetPassword',
  '/api/Accounts/ForgetPasswordByTenant',
  '/api/Accounts/SendOTP',
  '/api/Accounts/ValidateOtp',
  '/api/Accounts/ResetPassword',
  '/api/Accounts/ValidateChangePassword',
  '/api/Accounts/SetFirstPassword',
  '/api/Accounts/RequestActivation',
  '/api/Accounts/AcceptInvitation',
  '/api/Tenants/RegisterTenant',
  '/api/Tenants/RegistrationQuote',
  '/api/Packages/PublicCatalog',
  '/api/WebsiteContent',
];

export const handleRequestHeaderInterceptor: HttpInterceptorFn = (req, next) => {
  const tokens = inject(TokenStorageService);
  const translate = inject(TranslateService);
  const stored = readStoredLanguage();
  const fromTranslate = translate.getCurrentLang() || translate.getFallbackLang() || 'en-US';
  const currentLang = normalizeStoredLanguage(stored ?? fromTranslate);

  const shouldSetJsonContentType = () => {
    if (req.headers.has('Content-Type')) return false;
    if (req.method === 'GET' || req.method === 'HEAD') return false;
    const body = req.body;
    if (body === null || body === undefined) return false;
    if (body instanceof FormData) return false;
    if (body instanceof Blob) return false;
    if (body instanceof ArrayBuffer) return false;
    if (typeof body === 'string') return false;
    return true;
  };

  const headerUpdates: Record<string, string> = {
    'Accept-Language': currentLang,
  };

  if (!req.headers.has('Accept')) {
    headerUpdates['Accept'] = 'application/json';
  }

  if (shouldSetJsonContentType()) {
    headerUpdates['Content-Type'] = 'application/json';
  }

  const token = tokens.getAccessToken();
  if (token && !req.headers.has('Authorization') && !shouldSkipAuthorization(req.url)) {
    headerUpdates['Authorization'] = `Bearer ${token}`;
  }

  return next(req.clone({ setHeaders: headerUpdates }));
};

const shouldSkipAuthorization = (url: string | null): boolean => {
  if (!url) {
    return false;
  }

  return AUTHORIZATION_EXCLUDED_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};
